import * as React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronLeft, Download, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { BeforeAfter } from '@/components/BeforeAfter';
import { useRenderJob } from '@/hooks/useRenderJob';
import { ApiError, createRender, signedRenderUrl } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { formatDateTime, formatDuration } from '@/lib/format';
import { useToast } from '@/components/Toaster';

export const RenderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: job, isLoading } = useRenderJob(id);
  const [correction, setCorrection] = React.useState('');

  const fixMutation = useMutation({
    mutationFn: async () => {
      if (!job || !correction.trim()) throw new Error('sem correção');
      const p = (job.params ?? {}) as Record<string, unknown>;
      return createRender({
        input_key: job.input_key,
        tier: job.tier as 'draft' | 'standard' | 'premium' | 'variants',
        scene: (p.scene as 'interior_sala') ?? 'interior_sala',
        time_of_day: (p.time_of_day as 'meio_dia') ?? 'entardecer',
        light_temp: (p.light_temp as 'none') === 'none' ? undefined : (p.light_temp as 'warm_3000k' | undefined),
        user_note: correction.trim(),
      });
    },
    onSuccess: (res) => {
      toast({ title: 'Correção enfileirada', description: 'Gerando novo render com os ajustes.', variant: 'success' });
      navigate(`/renders/${res.job_id}`);
      setCorrection('');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.body.code === 'INSUFFICIENT_CREDITS') {
        toast({ title: 'Créditos insuficientes', description: 'Adicione créditos para corrigir.', variant: 'destructive' });
        return;
      }
      toast({ title: 'Falha ao corrigir', description: (err as Error).message, variant: 'destructive' });
    },
  });

  const inputUrl = useQuery({
    queryKey: ['inputUrl', job?.input_key],
    enabled: !!job?.input_key,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from('inputs')
        .createSignedUrl(job!.input_key, 3600);
      if (error || !data) throw new Error(error?.message ?? 'sign failed');
      return data.signedUrl;
    },
  });

  const outputUrl = useQuery({
    queryKey: ['outputUrl', job?.output_key],
    enabled: !!job?.output_key,
    queryFn: () => signedRenderUrl(job!.output_key as string),
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  if (!job) return <div className="p-6">Render não encontrado.</div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6 lg:p-10">
      <Button asChild variant="ghost" size="sm">
        <Link to="/renders">
          <ChevronLeft className="size-4" /> Voltar
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Render {job.id.slice(0, 8)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {job.status === 'processing' || job.status === 'queued' ? (
            <div className="aspect-[4/3] rounded-md bg-shimmer animate-shimmer grid place-items-center text-sm text-muted-foreground">
              Processando · tempo estimado ~{formatDuration((job.params as { latency_hint?: number })?.latency_hint ?? 15)}
            </div>
          ) : null}

          {job.status === 'succeeded' && inputUrl.data && outputUrl.data ? (
            <BeforeAfter beforeSrc={inputUrl.data} afterSrc={outputUrl.data} alt={job.id} />
          ) : null}

          {job.status === 'failed' ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              Falhou: {job.error ?? 'erro desconhecido'}. Seus créditos foram devolvidos.
            </div>
          ) : null}

          <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Tier</dt>
              <dd className="font-medium">{job.tier}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Status</dt>
              <dd className="font-medium">{job.status}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Créditos</dt>
              <dd className="font-medium">{job.credits_debited}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Criado</dt>
              <dd className="font-medium">{formatDateTime(job.created_at)}</dd>
            </div>
          </dl>

          {job.status === 'succeeded' && outputUrl.data ? (
            <Button
              onClick={async () => {
                try {
                  const res = await fetch(outputUrl.data as string);
                  const blob = await res.blob();
                  const objUrl = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = objUrl;
                  a.download = `portico-${job.id.slice(0, 8)}.png`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
                } catch (e) {
                  toast({
                    title: 'Falha ao baixar',
                    description: e instanceof Error ? e.message : 'erro desconhecido',
                    variant: 'destructive',
                  });
                }
              }}
            >
              <Download className="size-4" /> Baixar render
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {job.status === 'succeeded' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Corrigir esse render</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Algo saiu diferente do que você queria? Descreva o ajuste em uma frase.
              Exemplos: <em>"deixa a luz mais quente ao entardecer"</em>,{' '}
              <em>"troca o sofá por um de veludo verde-oliva"</em>,{' '}
              <em>"menos contraste, mais suave"</em>.
            </p>
            <Textarea
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              placeholder="O que quer ajustar?"
              rows={3}
              disabled={fixMutation.isPending}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Gera um novo render com os mesmos parâmetros + sua correção. Custa {job.credits_debited} crédito{job.credits_debited === 1 ? '' : 's'}.
              </span>
              <Button
                onClick={() => fixMutation.mutate()}
                disabled={!correction.trim() || fixMutation.isPending}
              >
                <Wand2 className="size-4" />
                {fixMutation.isPending ? 'Enfileirando…' : 'Gerar correção'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
